import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { formatIndividualName } from 'src/app/core/utils/autocomplete-util';
import { HttpParamsObj } from 'src/app/core/utils/type-util';
import { ManualPatientEntryResponse } from '../models/ manual-patient-entry-response';
import { CMActivities } from '../models/cm-activities';
import { Demographic } from '../models/demographic';
import { Gender } from '../models/gender';
import { HospitalUserDetails } from '../models/hospital-user-details';
import { Insurer } from '../models/insurer';
import { JSONResponse } from '../models/json-response';
import { Language } from '../models/language';
import { MemberAddress } from '../models/member-address';
import { Patient } from '../models/patient';
import { PatientCareGiver } from '../models/patient-care-giver';
import { PatientCareManager } from '../models/patient-care-manager';
import { PatientExternalAgencyUsers } from '../models/patient-external-agency-user';
import { Provider } from '../models/provider';
import { unformatPhoneNumbers } from '../utils/phone-number-util';

const getPatientDemographicsUrl = '/patients/getPatientDemograpgics.html';
const getOrganizationLanguagesUrl = '/patients/getOrganizationLanguages.html';
const addPatientAddressUrl = '/patients/addPatientAddress.html';
const checkPatientUrl = '/patients/checkPatient.html';
const getProviderListUrl = '/patients/getProviders.html';
const getCareGiversUrl = '/patients/getAllCareGivers.html';
const getCareManagersUrl = '/patients/getCareManagers.html';
const getExternalAgencyUsersUrl = '/patients/getExternalAgencyUsers.html';
const getHospitalUserDetailsListUrl = '/patients/getHospitalUserDetailsList.html';
const getInsurersUrl = '/patients/getInsurers.html';
const getGendersUrl = '/patients/getGenders.html';
const mrnExistsUrl = '/patients/mrnExists.html';
const rinExistsUrl = '/patients/rinExists.html';
const getMemberIdByDisplayIdUrl = '/patients/getMemberIdByDisplayId.html';
const saveManualPatientEntryUrl = '/patients/saveManualPatientEntry.html';
const updateManualPatientEntryUrl = '/patients/updateManualPatientEntry.html';
const upsertMemberAddressUrl = '/patients/upsertMemberAddress.html';
const saveMemberSearchHistoryUrl = '/patients/saveMemberSearchHistory.html';
const getMemberSnapshotCMActivitiesUrl = '/patients/getMemberSnapshotCMActivities.html';
const addCCHHRSReviewUrl = '/patients/addCCHHRSReview.html';

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private patientCache: Map<string, Patient> = new Map();

  private _addressUpdated: Subject<void> = new Subject();
  addressUpdated: Observable<void> = this._addressUpdated.asObservable();

  private _patientDetailsUpdated: Subject<void> = new Subject();
  patientDetailsUpdated: Observable<void> = this._patientDetailsUpdated.asObservable();

  private _cchHrsRecordUpdated: Subject<void> = new Subject();
  cchHrsRecordUpdated: Observable<void> = this._cchHrsRecordUpdated.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Fetches a patient.
   *
   * @param memberId `memberId` of patient
   * @param useCache Whether or not to try fetching the patient from `PatientService`'s
   *                 in-memory cache. The request will continue normally if the cache does
   *                 not contain the specified `memberId`.
   *
   *                 Defaults to `true`.
   */
  getPatient(memberId: string, useCache = true): Observable<Patient> {
    memberId = memberId.toUpperCase();

    if (this.patientCache.has(memberId) && useCache) {
      return of(this.patientCache.get(memberId));
    }

    return this.http
      .post<Patient>(getPatientDemographicsUrl, '', {
        params: {
          patientId: memberId
        }
      })
      .pipe(tap((patient) => this.patientCache.set(memberId, patient)));
  }

  getCachedPatient(memberId: string): Patient {
    return this.patientCache.get(memberId);
  }

  getPatientDisplayId(memberId: string) {
    return this.patientCache.get(memberId.toUpperCase())?.displayId;
  }

  getPatientName(memberId: string, showPreferredName = false): Observable<string> {
    return this.getPatient(memberId).pipe(
      map((patient) => {
        const baseName = formatIndividualName(patient);
        return showPreferredName && patient.preferredMemberName
          ? `${baseName} (${patient.preferredMemberName})`
          : baseName;
      })
    );
  }

  getPatientPronoun(memberId: string): Observable<string> {
    return this.getPatient(memberId).pipe(
      map((patient) => {
        return patient.preferredpronoun
          ? patient.preferredpronoun === 'Other'
            ? `${patient.preferredpronoun} (${patient.preferredPronounOther})`
            : patient.preferredpronoun
          : '';
      })
    );
  }

  memberIdExists(memberId: string): Observable<boolean> {
    return this.http
      .get<JSONResponse<string>>(checkPatientUrl, {
        params: {
          memberId: memberId || ''
        }
      })
      .pipe(map((response) => response.result === 'patient found'));
  }

  uncachePatient(memberId: string) {
    memberId = memberId.toUpperCase();

    this.patientCache.delete(memberId);
  }

  getOrganizationLanguages(): Observable<Language[]> {
    return this.http.post<Language[]>(getOrganizationLanguagesUrl, '', {
      params: {
        languagecode: ''
      }
    });
  }

  addPatientAddress(patientId: string, demographic: Demographic): Observable<JSONResponse<number>> {
    return this.http
      .post<JSONResponse<number>>(addPatientAddressUrl, demographic, {
        params: {
          memberID: patientId
        }
      })
      .pipe(
        tap(() => {
          this._addressUpdated.next();
        })
      );
  }

  getProviders(providerName: string, limit = 10): Observable<Provider[]> {
    return this.http.get<Provider[]>(getProviderListUrl, {
      params: {
        name: providerName,
        maxlimit: limit
      }
    });
  }

  getCareGivers(name: string, lookForUsername = false, limit = 10): Observable<PatientCareGiver[]> {
    return this.http.get<PatientCareGiver[]>(getCareGiversUrl, {
      params: {
        name: name,
        maxlimit: limit,
        lookForUsername: lookForUsername
      }
    });
  }

  getCareManagers(
    name: string,
    memberId?: string,
    entityRole = '',
    lookForUsername = false,
    limit = 10
  ): Observable<PatientCareManager[]> {
    const params: HttpParamsObj = {
      name: name,
      maxlimit: limit,
      entityRole: entityRole || '',
      lookForUsername: lookForUsername
    };

    if (memberId) {
      params.memberId = memberId;
    }

    return this.http.get<PatientCareManager[]>(getCareManagersUrl, {
      params: params
    });
  }

  getExternalAgencyUsers(
    name: string,
    memberId?: string,
    entityRole = '',
    lookForUsername = false,
    limit = 10
  ): Observable<PatientExternalAgencyUsers[]> {
    const params: HttpParamsObj = {
      name: name,
      maxlimit: limit,
      entityRole: entityRole || '',
      lookForUsername: lookForUsername
    };

    if (memberId) {
      params.memberId = memberId;
    }

    return this.http.get<PatientExternalAgencyUsers[]>(getExternalAgencyUsersUrl, {
      params: params
    });
  }

  getHospitalUserDetails(
    name: string,
    memberId?: string,
    entityRole = '',
    lookForUsername = false,
    limit = 10
  ): Observable<HospitalUserDetails[]> {
    const params: HttpParamsObj = {
      name: name,
      maxlimit: limit,
      entityRole: entityRole || '',
      lookForUsername: lookForUsername
    };

    if (memberId) {
      params.memberId = memberId;
    }

    return this.http.get<HospitalUserDetails[]>(getHospitalUserDetailsListUrl, {
      params: params
    });
  }

  clearCache() {
    this.patientCache.clear();
  }

  getGenders(): Observable<Gender[]> {
    return this.http.get<Gender[]>(getGendersUrl);
  }

  getInsurers(planIds: string[]): Observable<Insurer[]> {
    return this.http.get<Insurer[]>(getInsurersUrl, {
      params: {
        planIds: planIds
      }
    });
  }

  mrnExists(mrn: string, medicalHome: string): Observable<boolean> {
    if (mrn && medicalHome) {
      return this.http.get<boolean>(mrnExistsUrl, {
        params: {
          mrn: (mrn || '').trim(),
          medicalHome: (medicalHome || '').trim()
        }
      });
    }

    return of(false);
  }

  rinExists(rin: string): Observable<boolean> {
    if (rin) {
      return this.http.get<boolean>(rinExistsUrl, {
        params: {
          rin: (rin || '').trim()
        }
      });
    }

    return of(false);
  }

  saveManualPatientEntry(patient: Patient): Observable<ManualPatientEntryResponse> {
    const patientClone = cloneDeep(patient);
    unformatPhoneNumbers(patientClone);

    return this.http.post<ManualPatientEntryResponse>(saveManualPatientEntryUrl, patientClone);
  }

  updateManualPatientEntry(patient: Patient): Observable<Patient> {
    return this.http.post<Patient>(updateManualPatientEntryUrl, patient).pipe(
      tap(() => {
        this._patientDetailsUpdated.next();
      })
    );
  }

  getMemberIdByDisplayId(displayId: string): Observable<string> {
    return this.http.get(getMemberIdByDisplayIdUrl, {
      responseType: 'text',
      params: {
        displayId: displayId
      },
      headers: {
        Accept: 'text/plain, */*'
      }
    });
  }

  saveMemberSearchHistory(memberId: string) {
    return this.http.post(saveMemberSearchHistoryUrl, '', {
      params: {
        memberId: memberId
      }
    });
  }

  upsertMemberAddress(memberAddress: MemberAddress): Observable<JSONResponse<number | string>> {
    return this.http.post<JSONResponse<number>>(upsertMemberAddressUrl, memberAddress).pipe(
      tap(() => {
        this._addressUpdated.next();
      })
    );
  }

  getMemberSnapshotCMActivities(memberId: string): Observable<CMActivities> {
    return this.http.get<CMActivities>(getMemberSnapshotCMActivitiesUrl, {
      params: {
        memberId: memberId
      }
    });
  }

  addCCHHRSReview(memberId: string): Observable<boolean> {
    return this.http
      .post<boolean>(addCCHHRSReviewUrl, '', {
        params: {
          memberId: memberId
        }
      })
      .pipe(
        tap(() => {
          this._cchHrsRecordUpdated.next();
        })
      );
  }
}
